import React, { useEffect } from 'react';
import { Link as RouterLink, Navigate, useLocation, useParams } from 'react-router-dom';
import {
    Grid,
    Link,
    Paper,
    PaperProps,
    Skeleton,
    Tooltip,
    TooltipProps,
    Typography,
    styled,
    useTheme,
} from '@mui/material';

import { useAppDispatch, useAppSelector } from '../state/hooks';
import { fetchShipperHealthDetails } from '../state/features/shipperHealthSlice';
import { publicRoutes } from '../routes/appRouteList';
import { ApiStatus, Grade } from '../utils/enums';
import { formatNumber, formatRank, formatCurrency } from '../utils/formattingUtils';
import { buildShipperLink } from '../utils/navigationHelpers';
import { formatHealthFilters } from '../utils/stateHelpers';
import { getGradeColor } from '../utils/styleUtils';
import { useFilters } from '../components/Filters/useFilters';
import Hidden from '../components/layout/hidden';
import HealthToolbar from '../components/Toolbars/healthToolbar';
import GradeLabel from '../components/Labels/GradeLabel';
import ShipperHealthMap from '../components/Map/ShipperHealthMap';
import ShipperHealthLanesTable from '../components/Tables/ShipperHealthLanesTable';

interface StyleProps extends PaperProps {
    freightOrderCountGrade: Grade | null;
    averageLoadHoursDurationGrade: Grade | null;
    averageScheduledVsActualMilesPerHourGrade: Grade | null;
}

const classesPrefix = 'shipperHealthDetails';

const classes = {
    pageRoot: `${classesPrefix}-pageRoot`,
    pageRootItem: `${classesPrefix}-pageRootItem`,
    details: `${classesPrefix}-details`,
    volume: `${classesPrefix}-volume`,
    loadingHours: `${classesPrefix}-loadingHours`,
    mph: `${classesPrefix}-mph`,
    helpText: `${classesPrefix}-helpText`,
    title: `${classesPrefix}-title`,
};

const StyledGrid = styled(
    Grid,
    {
        // This prevents passing the StyleProps down to the component
        shouldForwardProp: (prop) => {
            return prop !== 'freightOrderCountGrade' &&
                prop !== 'averageLoadHoursDurationGrade' &&
                prop !== 'averageScheduledVsActualMilesPerHourGrade';
        },
    }
)<StyleProps>((
    {
        freightOrderCountGrade,
        averageLoadHoursDurationGrade,
        averageScheduledVsActualMilesPerHourGrade,
        theme,
    }
) => {
    return {
        [`&.${classes.pageRoot}`]: {
            minHeight: '100%',
        },
        [`& .${classes.pageRootItem}`]: {
            overflow: 'auto',
            margin: theme.spacing(2),
        },
        [`& .${classes.details}`]: {
            padding: theme.spacing(2),
        },
        [`& .${classes.volume}`]: {
            color: getGradeColor(freightOrderCountGrade, theme),
        },
        [`& .${classes.loadingHours}`]: {
            color: getGradeColor(averageLoadHoursDurationGrade, theme),
        },
        [`& .${classes.mph}`]: {
            color: getGradeColor(averageScheduledVsActualMilesPerHourGrade, theme),
        },
    };
});

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => {
    return <Tooltip {...props} classes={{ popper: className }} />;
})(() => {
    return {
        [`& .${classes.helpText}`]: {
            fontSize: '0.75rem',
        },
        [`& .${classes.title}`]: {
            fontWeight: 700,
        },
    };
});

const ShipperHealthDetails = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const location = useLocation();
    const params = useParams<{ id: string; }>();
    const filters = useFilters();

    const shipperHealthDetailsApiStatus = useAppSelector((state) => { return state.shipperHealth.shipperHealthDetailsApiStatus; });
    const shipperHealthDetails = useAppSelector((state) => { return state.shipperHealth.shipperHealthDetails; });

    const shipperHealthLanesApiStatus = useAppSelector((state) => { return state.shipperHealth.shipperHealthLanesApiStatus; });
    const shipperHealthLanes = useAppSelector((state) => { return state.shipperHealth.shipperHealthLanes; });

    useEffect(() => {
        if (params.id) {
            dispatch(fetchShipperHealthDetails({ shipperId: params.id }));
        }
    }, [dispatch, params.id]);

    if (shipperHealthDetailsApiStatus === ApiStatus.Failure) {
        return (
            <Navigate
                to={`${publicRoutes.notFound}?requestedroute=${encodeURIComponent(`${location.pathname}${location.search}`)}&reason=${encodeURIComponent(`We tried, but we couldn't find an shipper with ID ${params.id}.`)}`}
            />
        );
    }

    const overallGradeTooltip = (
        <Typography variant='body2' className={classes.helpText}>
            <span>The </span>
            <span className={classes.title}>Overall Grade</span>
            <span> for a shipper is an average of the Load Time, In Transit Delay, and Scheduled vs Actual MPH grades.</span>
        </Typography>
    );

    const shipperDetails = shipperHealthDetails.pickup_parties[0];
    const healthFilters = formatHealthFilters();

    return (
        <StyledGrid
            container
            className={classes.pageRoot}
            justifyContent='flex-start'
            direction='column'
            freightOrderCountGrade={shipperDetails?.freight_order_count_grade}
            averageLoadHoursDurationGrade={shipperDetails?.average_load_hours_duration_grade}
            averageScheduledVsActualMilesPerHourGrade={shipperDetails?.average_scheduled_vs_actual_miles_per_hour_grade}
        >
            <Grid item xs className={classes.pageRootItem}>
                <Paper>
                    <HealthToolbar pageTitle='Shipper Health Assessment' />
                    <Grid container>
                        <Grid item xs={12} md={7}>
                            <Grid
                                container
                                spacing={4}
                                className={classes.details}
                                sx={{
                                    alignItems: 'center',
                                }}
                            >
                                <Hidden direction='only' breakpoint='xs'>
                                    <Grid item>
                                        <StyledTooltip title={overallGradeTooltip}>
                                            <GradeLabel
                                                apiStatus={shipperHealthDetailsApiStatus}
                                                grade={shipperDetails?.pickup_party_health_grade}
                                                variant='h1'
                                            />
                                        </StyledTooltip>
                                    </Grid>
                                </Hidden>
                                <Grid item>
                                    <Typography variant='h4'>
                                        {
                                            shipperHealthDetailsApiStatus === ApiStatus.Idle || shipperHealthDetailsApiStatus === ApiStatus.Loading ?
                                                <Skeleton width={150} /> :
                                                (
                                                    <Link
                                                        component={RouterLink}
                                                        to={buildShipperLink(shipperDetails?.pickup_party_id, {
                                                            ...filters,
                                                            earliest_delivery_departure_timestamp: healthFilters.earliest_delivery_departure_timestamp,
                                                            latest_delivery_departure_timestamp: healthFilters.latest_delivery_departure_timestamp,
                                                        })}
                                                    >
                                                        {shipperDetails?.pickup_party_name}
                                                    </Link>
                                                )
                                        }
                                    </Typography>
                                    <Hidden direction='up' breakpoint='sm'>
                                        <StyledTooltip title={overallGradeTooltip}>
                                            <GradeLabel
                                                apiStatus={shipperHealthDetailsApiStatus}
                                                grade={shipperDetails?.pickup_party_health_grade}
                                                variant='h1'
                                            />
                                        </StyledTooltip>
                                    </Hidden>
                                    <Typography variant='h6'>Revenue (Last 30 days)</Typography>
                                    <Typography variant='h5'>
                                        {
                                            shipperHealthDetailsApiStatus === ApiStatus.Idle || shipperHealthDetailsApiStatus === ApiStatus.Loading ?
                                                <Skeleton width={120} /> :
                                                formatCurrency(shipperDetails?.linehaul_revenue_usd_amount)
                                        }
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Grid
                                container
                                className={classes.details}
                                sx={{
                                    alignItems: 'center',
                                }}
                            >
                                <Grid item xs={12}>
                                    <Typography variant='h5'>Performance</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid
                                        container
                                        spacing={1}
                                        className={classes.details}
                                        sx={{
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Grid item>
                                            <Typography variant='h6'>Volume</Typography>
                                            <Typography variant='h6'>Loading</Typography>
                                            <Typography variant='h6'>Scheduled MPH vs. Actual MPH</Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant='h6'>
                                                {
                                                    shipperHealthDetailsApiStatus === ApiStatus.Idle || shipperHealthDetailsApiStatus === ApiStatus.Loading ?
                                                        <Skeleton width={100} /> :
                                                        formatRank(shipperDetails?.freight_order_count_rank)
                                                }
                                            </Typography>
                                            <Typography variant='h6'>
                                                {
                                                    shipperHealthDetailsApiStatus === ApiStatus.Idle || shipperHealthDetailsApiStatus === ApiStatus.Loading ?
                                                        <Skeleton width={100} /> :
                                                        formatRank(shipperDetails?.average_load_hours_duration_rank)
                                                }
                                            </Typography>
                                            <Typography variant='h6'>
                                                {
                                                    shipperHealthDetailsApiStatus === ApiStatus.Idle || shipperHealthDetailsApiStatus === ApiStatus.Loading ?
                                                        <Skeleton width={100} /> :
                                                        formatRank(shipperDetails?.average_scheduled_vs_actual_miles_per_hour_rank)
                                                }
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <StyledTooltip
                                                title={
                                                    <div>
                                                        <Typography variant='body2' gutterBottom className={classes.helpText}>
                                                            <span className={classes.title}>Volume</span>
                                                            <span> is the count of orders for the lane in the past 30 days.</span>
                                                        </Typography>
                                                        <Typography variant='body2' className={classes.helpText}>
                                                            The grade thresholds for volume are dynamically calculated based on the operating data set&apos;s maximum daily volume on the lane for the past 30 days:
                                                        </Typography>
                                                        <ul>
                                                            <li>
                                                                <span className={classes.title}>A</span>
                                                                <span> = 75% or more of the maximum daily volume</span>
                                                            </li>
                                                            <li>
                                                                <span className={classes.title}>B</span>
                                                                <span> = 50% to 75% of the maximum daily volume</span>
                                                            </li>
                                                            <li>
                                                                <span className={classes.title}>D</span>
                                                                <span> = 25% to 50% of the maximum daily volume</span>
                                                            </li>
                                                            <li>
                                                                <span className={classes.title}>E</span>
                                                                <span> = Less than 25% of the maximum daily volume or an average daily volume less than 2</span>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                }
                                            >
                                                <Typography variant='h6' className={classes.volume}>
                                                    {
                                                        shipperHealthDetailsApiStatus === ApiStatus.Idle || shipperHealthDetailsApiStatus === ApiStatus.Loading ?
                                                            <Skeleton width={80} /> :
                                                            formatNumber(shipperDetails?.freight_order_count, 0, 0)
                                                    }
                                                </Typography>
                                            </StyledTooltip>

                                            <StyledTooltip
                                                title={
                                                    <div>
                                                        <Typography variant='body2' gutterBottom className={classes.helpText}>
                                                            <span className={classes.title}>Load Time</span>
                                                            <span> is the number of hours spent at a pickup location.</span>
                                                        </Typography>
                                                        <Typography variant='body2' className={classes.helpText}>
                                                            The grade thresholds for Load Time are based on the amount of time from arrival at the pickup location to departing the pickup location:
                                                        </Typography>
                                                        <ul>
                                                            <li>
                                                                <span className={classes.title}>A</span>
                                                                <span> = Less than 30 minutes</span>
                                                            </li>
                                                            <li>
                                                                <span className={classes.title}>C</span>
                                                                <span> = Between 30 minutes and 2 hours</span>
                                                            </li>
                                                            <li>
                                                                <span className={classes.title}>E</span>
                                                                <span> = Greater than 2 hours</span>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                }
                                            >
                                                <Typography variant='h6' className={classes.loadingHours}>
                                                    {
                                                        shipperHealthDetailsApiStatus === ApiStatus.Idle || shipperHealthDetailsApiStatus === ApiStatus.Loading ?
                                                            <Skeleton width={80} /> :
                                                            formatNumber(shipperDetails?.load_hours_duration !== null && shipperDetails?.freight_order_count ?
                                                                shipperDetails.load_hours_duration / shipperDetails.freight_order_count :
                                                                null, 2, 2)
                                                    }
                                                </Typography>
                                            </StyledTooltip>

                                            <StyledTooltip
                                                title={
                                                    <div>
                                                        <Typography variant='body2' gutterBottom className={classes.helpText}>
                                                            <span className={classes.title}>Scheduled vs Actual Miles Per Hour</span>
                                                            <span> evaluates the difference between Scheduled MPH and Actual MPH where a quotient of 1 is presumed to be delivering on time.</span>
                                                        </Typography>
                                                        <Typography variant='body2' className={classes.helpText}>
                                                            The grade thresholds for Scheduled vs Actual MPH are dynamically calculated based on the median distance from 1, but all ratios of less than 1 receive an E:
                                                        </Typography>
                                                        <ul>
                                                            <li>
                                                                <span className={classes.title}>A</span>
                                                                <span> = 2 or more median deviations over 1</span>
                                                            </li>
                                                            <li>
                                                                <span className={classes.title}>B</span>
                                                                <span> = 1 to 2 median deviations over 1</span>
                                                            </li>
                                                            <li>
                                                                <span className={classes.title}>D</span>
                                                                <span> = Between 1 and 1 median deviation over 1</span>
                                                            </li>
                                                            <li>
                                                                <span className={classes.title}>E</span>
                                                                <span> = Actual MPH &#247; Scheduled MPH &lt; 1; or Actual MPH or Scheduled MPH &gt; 75</span>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                }
                                            >
                                                <Typography variant='h6' className={classes.mph}>
                                                    {
                                                        shipperHealthDetailsApiStatus === ApiStatus.Idle || shipperHealthDetailsApiStatus === ApiStatus.Loading ?
                                                            <Skeleton width={80} /> :
                                                            formatNumber(shipperDetails?.average_scheduled_vs_actual_miles_per_hour, 2, 2)
                                                    }
                                                </Typography>
                                            </StyledTooltip>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <ShipperHealthMap
                                isFetchingData={shipperHealthLanesApiStatus === ApiStatus.Idle || shipperHealthLanesApiStatus === ApiStatus.Loading}
                                theme={theme}
                                lanes={shipperHealthLanes.lanes}
                            />
                        </Grid>
                        <Grid item xs={12} className={classes.details}>
                            <Typography variant='h5' gutterBottom>Lanes</Typography>
                            <ShipperHealthLanesTable />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </StyledGrid>
    );
};

export default ShipperHealthDetails;
